@if (loadState() === 'error') {
  <hlm-alert tone="error">
    <hlm-icon name="lucideCircleAlert" size="1rem" class="mt-0.5 text-destructive" />
    <span>Could not load the feedback form.</span>
  </hlm-alert>
} @else if (loadState() === 'ready') {
  <div class="rounded-lg border border-border bg-card p-3">
    <div class="flex items-center justify-between gap-2">
      <span class="text-base font-medium text-foreground">
        @if (open()) {
          Transcript feedback
        } @else if (saved()) {
          Feedback sent
        } @else {
          Feedback
        }
      </span>
      <button hlmBtn type="button" variant="outline" size="sm" (click)="toggleOpen()">
        @if (open()) { Hide } @else { Show }
      </button>
    </div>

    @if (open() && !showForm() && saved()) {
      <div class="mt-2 flex items-center justify-between gap-2">
        <span class="text-base text-muted-foreground">Thank you. You said: {{ savedSummary() }}</span>
        <button hlmBtn type="button" variant="outline" size="sm" (click)="startChange()">Change</button>
      </div>
    }

    @if (open() && showForm()) {
      <div class="mt-2 space-y-3">
        @if (steps().length > 1) {
          <div class="text-sm text-muted-foreground">Step {{ stepIdx() + 1 }} of {{ steps().length }}</div>
        }

        @for (q of stepQuestions(); track q.key) {
          <fieldset class="space-y-1.5">
            <legend class="text-base font-medium text-foreground">
              {{ q.text.en }}
              @if (q.hint) {
                <span aria-hidden="true" class="text-muted-foreground">ⓘ</span>
              }
            </legend>
            @if (q.hint) {
              <p class="text-sm text-muted-foreground">{{ q.hint.en }}</p>
            }
            @switch (q.type) {
              @case ('buttons') {
                <div class="flex flex-wrap gap-1.5">
                  @for (o of q.options; track o.key) {
                    <button
                      hlmBtn
                      type="button"
                      size="sm"
                      [variant]="isSelected(q, o) ? 'default' : 'outline'"
                      [attr.aria-pressed]="isSelected(q, o)"
                      [attr.title]="o.hint?.en ?? null"
                      (click)="setAnswer(q, o.key)"
                    >
                      {{ o.label.en }}
                      @if (o.hint) {
                        <span aria-hidden="true">ⓘ</span>
                        <span class="sr-only">{{ o.hint.en }}</span>
                      }
                    </button>
                  }
                </div>
              }
              @case ('checkboxes') {
                <div class="grid gap-1.5 sm:grid-cols-2">
                  @for (o of q.options; track o.key) {
                    <div [class]="optionClass(q, o)" [attr.title]="o.hint?.en ?? null">
                      <hlm-checkbox [checked]="isSelected(q, o)" (checkedChange)="setAnswer(q, o.key)">
                        {{ o.label.en }}
                        @if (o.hint) {
                          <span aria-hidden="true" class="text-muted-foreground">ⓘ</span>
                          <span class="sr-only">{{ o.hint.en }}</span>
                        }
                      </hlm-checkbox>
                    </div>
                  }
                </div>
              }
              @default {
                <hlm-radio-group orientation="horizontal" [value]="selectedKey(q)" (valueChange)="setAnswer(q, $any($event))">
                  @for (o of q.options; track o.key) {
                    <div [class]="optionClass(q, o)" [attr.title]="o.hint?.en ?? null">
                      <hlm-radio [value]="o.key">
                        {{ o.label.en }}
                        @if (o.hint) {
                          <span aria-hidden="true" class="text-muted-foreground">ⓘ</span>
                          <span class="sr-only">{{ o.hint.en }}</span>
                        }
                      </hlm-radio>
                    </div>
                  }
                </hlm-radio-group>
              }
            }
          </fieldset>
        }

        @if (isLast()) {
          <label class="block space-y-1">
            <span class="text-base font-medium text-foreground">
              Anything else? <span class="text-muted-foreground">(optional)</span>
            </span>
            <textarea hlmTextarea rows="2" maxlength="2000" [value]="comment()" (input)="onComment($event)"></textarea>
          </label>
        }

        @if (sendError(); as error) {
          <hlm-alert [tone]="sendErrorTone()">{{ error }}</hlm-alert>
        }

        <div class="flex items-center gap-1.5">
          @if (stepIdx() > 0) {
            <button hlmBtn type="button" variant="outline" size="sm" (click)="back()">Back</button>
          }
          @if (isLast()) {
            <button hlmBtn type="button" size="sm" [disabled]="!complete() || sending()" (click)="send()">
              Send
            </button>
          } @else {
            <button hlmBtn type="button" size="sm" [disabled]="!complete()" (click)="next()">Next</button>
          }
          @if (saved()) {
            <button hlmBtn type="button" variant="outline" size="sm" (click)="cancel()">Cancel</button>
          }
        </div>
      </div>
    }
  </div>
}
